import { useMemo } from 'react';
import { readClientNotes, updateClientNotes, useClientNotes } from '../../../views/Advisor/ClientV2/clientNotesStore';

/**
 * Smart IDEA 異議處理 — what a client may push back with, from two places:
 *
 *  A · 客戶提過: the adviser marks, from a fixed list, what the client actually
 *      said. Stored on the server as factsheet.smartIdea.objectionMarks.
 *  B · 可能會提: inferred, never typed — from factsheet fields and from the
 *      client's own 保障小測驗 answers. A marked objection is not repeated here,
 *      and one that two independent sources point at ranks first.
 *
 * Every objection's answer and script are fixed copy
 * (`smart-idea-objection-<id>[-answer|-script]`), so the list below is the
 * whole vocabulary: a new objection is a new id plus three messages.
 */

export const CLIENT_OBJECTIONS = [
  'price',
  'company',
  'spouse',
  'think',
  'compare',
  'otherAgent',
  'noNeed',
  'distrust'
] as const;
export type ClientObjectionId = (typeof CLIENT_OBJECTIONS)[number];

const isObjectionId = (value: unknown): value is ClientObjectionId =>
  typeof value === 'string' && (CLIENT_OBJECTIONS as readonly string[]).includes(value);

/** The factsheet fields the rules read. All optional: most factsheets are half-filled. */
export type ObjectionFacts = {
  marriageStatus?: string | null;
  monthlyIncome?: number | string | null;
  monthlyExpense?: number | string | null;
  existingClient?: string | null;
  age?: number | null;
};

/**
 * A reason is a message id (plus values) and where it came from. A quiz reason
 * also carries `parts`: the message ids of the answers behind it, joined into
 * the message's {why} when rendered — one reason per source, however many
 * answers agree.
 */
export type ObjectionReason = {
  source: 'facts' | 'quiz';
  messageId: string;
  values?: Record<string, string | number>;
  parts?: string[];
};
export type InferredObjection = { id: ClientObjectionId; reasons: ObjectionReason[] };

export const INFERRED_LIMIT = 3;
/** Monthly expense at or above this share of income predicts 「保費太貴」. */
export const EXPENSE_RATIO = 0.7;
/** Under this age, and single, predicts 「還年輕，暫時不需要」. */
export const YOUNG_AGE = 30;

const toNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** The factsheet rules. Each returns nothing when its field is missing — no guessing. */
export const factObjections = (facts: ObjectionFacts): { id: ClientObjectionId; reason: ObjectionReason }[] => {
  const out: { id: ClientObjectionId; reason: ObjectionReason }[] = [];
  const reason = (messageId: string, values?: ObjectionReason['values']): ObjectionReason => ({
    source: 'facts',
    messageId,
    values
  });

  if (facts.marriageStatus === 'Married')
    out.push({ id: 'spouse', reason: reason('smart-idea-objection-why-married') });

  const income = toNumber(facts.monthlyIncome);
  const expense = toNumber(facts.monthlyExpense);
  if (income && income > 0 && expense !== null && expense / income >= EXPENSE_RATIO) {
    out.push({
      id: 'price',
      reason: reason('smart-idea-objection-why-expense', { pct: Math.round((expense / income) * 100) })
    });
  }

  if (facts.existingClient === 'No') out.push({ id: 'compare', reason: reason('smart-idea-objection-why-new') });

  if (typeof facts.age === 'number' && facts.age < YOUNG_AGE && facts.marriageStatus === 'Single') {
    out.push({ id: 'noNeed', reason: reason('smart-idea-objection-why-young') });
  }
  return out;
};

/**
 * 可能會提: quiz reasons first (the client's own answers), then factsheet ones,
 * grouped per objection; more reasons rank higher, ties keep that order.
 */
export const inferObjections = (
  facts: ObjectionFacts,
  quizObjections: { id: string; reasons: string[] }[],
  marked: ClientObjectionId[],
  limit = INFERRED_LIMIT
): InferredObjection[] => {
  const groups: InferredObjection[] = [];
  const add = (id: ClientObjectionId, reason: ObjectionReason) => {
    if (marked.includes(id)) return;
    let group = groups.find(g => g.id === id);
    if (!group) {
      group = { id, reasons: [] };
      groups.push(group);
    }
    group.reasons.push(reason);
  };
  quizObjections.forEach(o => {
    if (isObjectionId(o.id)) {
      add(o.id, { source: 'quiz', messageId: 'smart-idea-objection-why-quiz', parts: o.reasons });
    }
  });
  factObjections(facts).forEach(({ id, reason }) => add(id, reason));

  return groups
    .map((group, index) => ({ group, index }))
    .sort((a, b) => b.group.reasons.length - a.group.reasons.length || a.index - b.index)
    .slice(0, limit)
    .map(({ group }) => group);
};

// ── A · marks, per client, on the factsheet (smartIdea.objectionMarks) ──────
const marksOf = (clientId?: string | null, notes = readClientNotes(clientId)): ClientObjectionId[] => {
  const raw = (notes && notes.smartIdea.objectionMarks) || [];
  return raw.filter(isObjectionId);
};

export const readMarkedObjections = (clientId?: string | null): ClientObjectionId[] => marksOf(clientId);

export const writeMarkedObjections = (clientId: string, marked: ClientObjectionId[]): void => {
  if (!clientId) return;
  void updateClientNotes(clientId, { smartIdea: { objectionMarks: marked } }, notes => ({
    ...notes,
    smartIdea: { ...notes.smartIdea, objectionMarks: marked }
  }));
};

export const useMarkedObjections = (
  clientId?: string | null
): [ClientObjectionId[], (next: ClientObjectionId[]) => void] => {
  const notes = useClientNotes(clientId);
  const marked = useMemo(() => marksOf(clientId, notes), [clientId, notes]);
  const update = (next: ClientObjectionId[]) => {
    if (clientId) writeMarkedObjections(clientId, next);
  };
  return [marked, update];
};
